import { useLocale } from '@app/core/hooks/useLocale';
import { ModalSize } from '@app/shared/components/modal/base-modal-enums';
import BaseModal from '@app/shared/components/modal/BaseModal';
import styles from '@app/modules/projects/dashboard/shared/per-year-capital-structure/per-year-capital-structure-chart.module.scss';
import { abbreviatedValueFormatter } from '@app/shared/formatters';
import {
  DenominationMultiplier,
  InstrumentBlockKey,
  InstrumentType,
} from '@app/shared/models/contracts/enums/shared-enums';
import { FC, useEffect, useState } from 'react';
import { useAppSelector } from '@app/core/hooks/redux-hooks';
import { PerYearCapitalStructureChartStructure } from '@app/modules/projects/dashboard/shared/per-year-capital-structure/per-year-capital-structure-chart-types';
import { FormProvider, useForm } from 'react-hook-form';
import { formConfigBase } from '@app/shared/constants/form-config-base';
import { Toggle, ToggleSize } from '@app/shared/components/toggle/Toggle';
import classNames from 'classnames';
import useInstrumentSort from '@app/shared/hooks/useInstrumentSort';
import SvgChevron from '@app/shared/icons/Chevron';
import PerYearCapitalStructureModalChart from './PerYearCapitalStructureModalChart';
import { PerYearCapitalStructureInstrumentTableSection } from './PerYearCapitalStructureInstrumentTableSection';
import { enumKeyByValue } from '@app/shared/helpers';

interface PerYearCapitalStructureModalProps {
  structure: PerYearCapitalStructureChartStructure | null;
  isOpen: boolean;
  onClose: () => void;
  isEntryStack: boolean;
  shouldScaleDenomination?: boolean;
  /**
   * The "% of total" column header/basis is a property of which chart opened
   * the modal, not of the sign of Net Debt on the selected point:
   * - Total Equity Chart (PWERM and OPM) never shows Net Debt, so the
   *   percentages are of Total Equity Proceeds - "Net Equity %".
   * - The three RWD stacked distribution charts (EV, Total Attributable
   *   Equity, All Instruments) always show Net Debt, so the percentages are
   *   of (Net Debt-inclusive) Net Exit Proceeds - "Net EV %".
   * Defaults to 'netEv' to match the RWD stacked distribution charts.
   */
  percentageBasis?: 'netEv' | 'netEquity';
}

export const PerYearCapitalStructureModal: FC<PerYearCapitalStructureModalProps> = ({
  structure,
  isOpen,
  onClose,
  isEntryStack = false,
  shouldScaleDenomination = true,
  percentageBasis = 'netEv',
}) => {
  const { l } = useLocale();
  const denomination = useAppSelector((state) => state.project.projectDraft.details.denomination);
  const multiplier = shouldScaleDenomination ? DenominationMultiplier[denomination] : 1;

  const [toggleExpanded, setToggleExpanded] = useState({
    ignoreExpandRows: false,
    toggleExpanded: false,
  });
  const defaultExpandedInstruments: Dictionary<boolean> = {};
  const allInstruments =
    structure?.ranks
      .flatMap((rank) => rank.instruments)
      .filter((instrument) => instrument.id !== InstrumentBlockKey.NetDebtId) ?? [];
  allInstruments.forEach((instrument) => {
    defaultExpandedInstruments[instrument.id] = false;
  });

  const [expandedInstruments, setExpandedInstruments] = useState<Dictionary<boolean>>(
    defaultExpandedInstruments
  );

  const expandAllRows = (isExpandAllRows: boolean) => {
    if (!structure) {
      return;
    }
    const expandedInstruments: Dictionary<boolean> = { title: isExpandAllRows };

    allInstruments.forEach((instrument) => {
      expandedInstruments[instrument.id] = isExpandAllRows;
    });
    setExpandedInstruments(expandedInstruments);
  };

  useEffect(() => {
    if (Object.keys(expandedInstruments).length > 0) {
      const values = Object.values(expandedInstruments);
      const allTrue = values.every((value) => value);
      const allFalse = values.every((value) => !value);
      if (!allTrue && !allFalse) {
        setToggleExpanded((prevState) => {
          if (!prevState.toggleExpanded && prevState.ignoreExpandRows) {
            return prevState;
          }
          return { toggleExpanded: false, ignoreExpandRows: true };
        });
      }
      if (allTrue) {
        setToggleExpanded((prevState) => {
          if (prevState.toggleExpanded && prevState.ignoreExpandRows) {
            return prevState;
          }
          return { toggleExpanded: true, ignoreExpandRows: true };
        });
      }
    }
  }, [expandedInstruments]);

  useEffect(() => {
    if (!toggleExpanded.ignoreExpandRows) {
      expandAllRows(toggleExpanded.toggleExpanded);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toggleExpanded]);

  const formMethods = useForm({
    ...formConfigBase,
  });

  const [selectedBlock, setSelectedBlock] = useState<string | null>(null);

  const netDebtInstruments = structure?.ranks.find((rank) => rank.rank === -1)?.instruments ?? [];
  const ordinaryEquityInstruments = structure?.ranks.flatMap((rank) =>
    rank.instruments
      .filter(
        (instrument) =>
          instrument.type === enumKeyByValue(InstrumentType, InstrumentType.OrdinaryEquity)
      )
      .map((instrument) => ({ ...instrument, rank: rank.rank, instrumentId: instrument.id }))
  );
  const sortedOrdinaryEquityInstruments = useInstrumentSort(ordinaryEquityInstruments ?? []);
  const shareholderLoanNotesInstruments = structure?.ranks.flatMap((rank) =>
    rank.instruments
      .filter(
        (instrument) =>
          instrument.type === enumKeyByValue(InstrumentType, InstrumentType.ShareholderLoanNotes)
      )
      .map((instrument) => ({ ...instrument, rank: rank.rank, instrumentId: instrument.id }))
  );
  const sortedShareholderLoanNotesInstruments = useInstrumentSort(
    shareholderLoanNotesInstruments ?? []
  );
  const preferredSharesInstruments = structure?.ranks.flatMap((rank) =>
    rank.instruments
      .filter(
        (instrument) =>
          instrument.type === enumKeyByValue(InstrumentType, InstrumentType.PreferredShares)
      )
      .map((instrument) => ({ ...instrument, rank: rank.rank, instrumentId: instrument.id }))
  );
  const sortedPreferredSharesInstruments = useInstrumentSort(preferredSharesInstruments ?? []);

  const isNetDebtPresent = netDebtInstruments.length > 0;
  const isOrdinaryEquityPresent = ordinaryEquityInstruments && ordinaryEquityInstruments.length > 0;
  const isShareholderLoanNotesPresent =
    shareholderLoanNotesInstruments && shareholderLoanNotesInstruments.length > 0;
  const isPreferredSharesPresent =
    preferredSharesInstruments && preferredSharesInstruments.length > 0;

  const hasInterimDistributions = (structure?.cumulativePvOfInterimCashFlows ?? 0) !== 0;
  const handleClose = () => {
    setExpandedInstruments({});
    setToggleExpanded({ ignoreExpandRows: false, toggleExpanded: false });
    setSelectedBlock(null);
    onClose();
  };

  return (
    <BaseModal
      size={ModalSize.Large}
      isOpen={isOpen}
      onCloseButtonClick={handleClose}
      shouldCloseOnOverlayClick
      onClick={() => setSelectedBlock(null)}
      onRequestClose={handleClose}>
      <>
        <div>
          <h2>{isEntryStack ? l('_EntryCapitalStructure') : l('_ExitCapitalStructure')}</h2>
          <div className={styles['table-flex']}>
            <div>
              {structure && (
                <PerYearCapitalStructureModalChart
                  structure={structure}
                  selectedBlock={selectedBlock}
                  setSelectedBlock={setSelectedBlock}
                />
              )}
            </div>
            <div className={styles['table-content']}>
              <div className={classNames(styles['header-flex'])}>
                <div
                  className={classNames(
                    styles['modal-instrument-title'],
                    styles['no-padding-left'],
                    'table-primary'
                  )}>
                  {hasInterimDistributions && (
                    <SvgChevron
                      onClick={(event) => {
                        event.stopPropagation();
                        setExpandedInstruments({
                          ...expandedInstruments,
                          title: !expandedInstruments.title,
                        });
                      }}
                      className={classNames(styles['chevron'], {
                        [styles['chevron__collapsed']]: !expandedInstruments.title,
                        [styles['chevron__expanded']]: expandedInstruments.title,
                      })}
                    />
                  )}
                  <h3 data-testid="total-distribution-title">
                    {isEntryStack
                      ? `${l('_DayOneEquityValue')} : ${abbreviatedValueFormatter({
                          value: (structure?.presentValueOfTotalEquity ?? 0) * multiplier,
                        })}`
                      : l('_TotalDistributionTitle', {
                          value: abbreviatedValueFormatter({
                            value: (structure?.presentValueOfTotalEquity ?? 0) * multiplier,
                          }),
                        })}
                  </h3>
                </div>
                <div
                  className={styles['toggle-wrapper']}
                  onClick={(event) => event.stopPropagation()}>
                  <FormProvider {...formMethods}>
                    <Toggle
                      name="expandAllRows"
                      label={l('_ExpandAllRows')}
                      onChange={() => {
                        setToggleExpanded({
                          toggleExpanded: !toggleExpanded.toggleExpanded,
                          ignoreExpandRows: false,
                        });
                      }}
                      toggleSize={ToggleSize.Medium}
                      forceChecked={toggleExpanded.toggleExpanded}
                    />
                  </FormProvider>
                </div>
              </div>
              {expandedInstruments.title && hasInterimDistributions && (
                <>
                  <div>
                    <div className={classNames(styles['modal-instrument-title'])}>
                      <SvgChevron
                        className={classNames(styles['chevron'], [styles['chevron__hidden']])}
                      />
                      <div>
                        {l('_InterimDistributionTitle', {
                          value: abbreviatedValueFormatter({
                            value: (structure?.cumulativePvOfInterimCashFlows ?? 0) * multiplier,
                          }),
                        })}
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className={classNames(styles['modal-instrument-title'])}>
                      <SvgChevron
                        className={classNames(styles['chevron'], [styles['chevron__hidden']])}
                      />
                      <div>
                        {l('_ExitDistributionTitle', {
                          value: abbreviatedValueFormatter({
                            value: (structure?.totalProceeds ?? 0) * multiplier,
                          }),
                        })}
                      </div>
                    </div>
                  </div>
                </>
              )}
              <table className={classNames('table-primary', styles['table-section'])}>
                <thead>
                  <tr>
                    <th
                      data-testid="component-header"
                      className={classNames(
                        'table-primary__cell--header-quinary',
                        'table-primary',
                        styles['header--min-width'],
                        styles['no-padding-left']
                      )}>
                      {l('_Component')}
                    </th>
                    <th
                      data-testid="weighted-ev-header"
                      className={classNames(
                        'table-primary__cell--header-quinary',
                        'table-primary__cell--right',
                        styles['header--min-width']
                      )}>
                      {isEntryStack ? l('_Amount') : l('_WeightedProceedsTitle')}
                    </th>
                    <th
                      data-testid="net-ev-header"
                      className={classNames(
                        'table-primary__cell--header-quinary',
                        'table-primary__cell--right',
                        styles['header--min-width']
                      )}>
                      {percentageBasis === 'netEquity'
                        ? l('_NetEquityPercentage')
                        : l('_NetEvPercentage')}
                    </th>
                    {!isEntryStack && (
                      <>
                        <th
                          data-testid="mom-header"
                          className={classNames(
                            'table-primary__cell--header-quinary',
                            'table-primary__cell--right',
                            styles['header--min-width']
                          )}>
                          {l('_MoM')}
                        </th>
                        <th
                          data-testid="irr-header"
                          className={classNames(
                            'table-primary__cell--header-quinary',
                            'table-primary__cell--right',
                            styles['header--min-width']
                          )}>
                          {l('_IRR')}
                        </th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {isNetDebtPresent && (
                    <PerYearCapitalStructureInstrumentTableSection
                      instrumentsType={null}
                      instrumentsData={netDebtInstruments}
                      isEntryStack={isEntryStack}
                      setExpandedInstruments={setExpandedInstruments}
                      expandedInstruments={expandedInstruments}
                      selectedBlock={selectedBlock}
                      shouldScaleDenomination={shouldScaleDenomination}
                      percentageBasis={percentageBasis}
                    />
                  )}
                  {isShareholderLoanNotesPresent && (
                    <PerYearCapitalStructureInstrumentTableSection
                      instrumentsType={InstrumentType.ShareholderLoanNotes}
                      instrumentsData={sortedShareholderLoanNotesInstruments}
                      isEntryStack={isEntryStack}
                      setExpandedInstruments={setExpandedInstruments}
                      expandedInstruments={expandedInstruments}
                      selectedBlock={selectedBlock}
                      shouldScaleDenomination={shouldScaleDenomination}
                      percentageBasis={percentageBasis}
                    />
                  )}
                  {isPreferredSharesPresent && (
                    <PerYearCapitalStructureInstrumentTableSection
                      instrumentsType={InstrumentType.PreferredShares}
                      instrumentsData={sortedPreferredSharesInstruments}
                      isEntryStack={isEntryStack}
                      setExpandedInstruments={setExpandedInstruments}
                      expandedInstruments={expandedInstruments}
                      selectedBlock={selectedBlock}
                      shouldScaleDenomination={shouldScaleDenomination}
                      percentageBasis={percentageBasis}
                    />
                  )}
                  {isOrdinaryEquityPresent && (
                    <PerYearCapitalStructureInstrumentTableSection
                      instrumentsType={InstrumentType.OrdinaryEquity}
                      instrumentsData={sortedOrdinaryEquityInstruments}
                      isEntryStack={isEntryStack}
                      setExpandedInstruments={setExpandedInstruments}
                      expandedInstruments={expandedInstruments}
                      selectedBlock={selectedBlock}
                      shouldScaleDenomination={shouldScaleDenomination}
                      percentageBasis={percentageBasis}
                    />
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </>
    </BaseModal>
  );
};
